import declare from "dojo/_base/declare";
import FormData from "csi/action/FormData";
import New from "csi/table/New";
import ActionControllerMixin from "csi/wizard/ActionControllerMixin";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import { CallMethod } from "csi/xml/CallMethod";
import SourcingOrders from "../main";
import { ButtonEnum } from "designSystem/types/button-types";
import { routerStore } from "csi/state/rootStore";

const {
	BUTTON_FINISH_GO,
} = ButtonEnum;
    
const INDEX_SHIPMENT_TERMS_VIEW = 0;
const INDEX_PURCHASED_ORDERS_VIEW = 1;
const INDEX_SHIPMENTS_VIEW_NO_PO = 1;
const INDEX_SHIPMENTS_VIEW_WITH_PO = 2;
const INDEX_FORM_NO_PO = 2;
const INDEX_FORM_WITH_PO = 3;

const NewPackListFromShipmentOrders = declare([New, ActionControllerMixin], {
	fdShipmentTermsPage : null,
	fdPOPage : null,
	fdShipmentsPage : null,
	fdPO: null,
	shipmentSelected: false,

	/**
         * @override
         * If shipment is preselected, skip first page of wizard
         */
	onWizardViewCreate: function(wizard, page, view) {
		this.inherited(arguments);
		const index = wizard.getIndexOfPage(page);

		if (index == 0 && this.shipmentSelected) {
			wizard.proxyNextPage();
		}
	},
        
	/**
         * Get selected shipmentTerms
         */
	getSelectedShipmentTerms: function() {
		return this.shipmentSelected ? this.getParent() : this.m_formData.getSelectedNode();
	},
        
	/**
         * Get selected purchasedOrder
         */
	getSelectedPO: function() {
		if (!!this.fdPOPage) {
			return this.fdPOPage.getSelectedNode();
		} else {
			return null;
		}
	},
        
	/**
         * Get selected shipments
         */
	getSelectedShipments: function() {
		if (!!this.fdShipmentsPage) {
			return this.fdShipmentsPage.getSelectedValue();
		} else {
			return null;
		}
	},
        
	/**
         * Check whether shipmentTerms is selected
         */
	isSelectedShipmentTerms: function() {
		if (!this.getSelectedShipmentTerms()) 
			return false ;
            
		return true;
	},
        
	/**
         * Check whether purchasedOrder is selected
         */
	isSelectedPO: function() {
		if (!!this.fdPOPage && !this.getSelectedPO()) {
			return false;
		}
            
		return true;
	},

	/**
         * @override
         * Enable next only if shipmentTerms is selected or PO are selected
         * @param {Widget} wizard
         * @param {Content} page
         * @param {Number} index
         */
	disableNext: function(wizard, page, index) {
		return index == INDEX_SHIPMENT_TERMS_VIEW ? !this.isSelectedShipmentTerms() : !this.isSelectedPO();
	},

	/**
         * @override
         * Disable back to first page if shipment is preselected
         */
	disablePrev: function(wizard, page, index) {
		return this.shipmentSelected && index == 1;
	},
        
	/**
         * @override
         * Enable finish only if shipmentTerms is selected or if shipmentTerms and PO is selected
         * @param {Widget} wizard
         * @param {Content} page
         * @param {Number} index
         */
	disableFinish: function(wizard, page, index) {
		if (SourcingOrders.isPOPackList() && index == INDEX_SHIPMENT_TERMS_VIEW) {
			return true;
		} 
		return this.disableNext(wizard, page, index); 
	},

	/**
         * Select ShipmentTerms node on form, setting it on the left tab
         * @param {Content} currentPage 
         * @param {Node} shipmentTerms 
         */
	selectShipmentTerms: function(currentPage, shipmentTerms) {
		const view = currentPage.viewTab;
		const ctx = view.getContext(shipmentTerms.$URL);
		this.m_formData.changeSelection(ctx, true, true);
		view.defView.setFormData(this.m_formData);
	},
        
	/**
         * @override
         * Prepare the data and pass it into the next page
         * @param {Widget} wizard
         * @param {Content} currentPage
         * @param {Content} nextPage
         * @param {Boolean} bFinish
         */
	onNextPage: function(wizard, currentPage, nextPage, bFinish) {
		let bRefresh: boolean = false;
		let fd: FormData = this.m_formData;
		const indexOfPage = wizard.getIndexOfPage(nextPage);

		const shipmentTerms = this.getSelectedShipmentTerms();
		if (indexOfPage == 0 && !this.shipmentSelected) {
			// moving to Products table
			fd = this.fdShipmentTermsPage = this.fdShipmentTermsPage || this.m_formData;
			fd.m_bRequireOne = true;
			fd.m_bSelectOne = true;
		} else if (SourcingOrders.isPOPackList() && indexOfPage == INDEX_PURCHASED_ORDERS_VIEW) {
			// initialize form data
			fd = this.fdPOPage = this.fdPOPage || new FormData(this);
			fd.m_bSelectOne = true;
			fd.m_bRequireOne = true;
			if (fd.getNode() != shipmentTerms) {
				fd.setNode(shipmentTerms);
				fd.resetAggregations();
				fd.resetSelections();
				bRefresh = true;
				if (this.shipmentSelected) {
					this.selectShipmentTerms(currentPage, shipmentTerms);
					// preselect PO
					const po = routerStore.getBreadcrumbNode("PurchasedOrder");
					if (po) {
						fd.changeSelection(this.widget.getContext(po.$URL), true);
					}
				}
			}
		} else {
			if ((SourcingOrders.isPOPackList() && indexOfPage == INDEX_SHIPMENTS_VIEW_WITH_PO) || (!SourcingOrders.isPOPackList() && indexOfPage == INDEX_SHIPMENTS_VIEW_NO_PO)) {
				// initialize form data
				// shipments page, previous page: either PurchasedOrders (when setting SourcingOrders.isPOPackList() = true) or ShipmentTerms (when SourcingOrders.isPOPackList() = false)
				fd = this.fdShipmentsPage = this.fdShipmentsPage || new FormData(this);
				if (fd.getNode() != shipmentTerms || this.fdPO != this.getSelectedPO()) {
					this.fdPO = this.getSelectedPO();
					fd.setNode(shipmentTerms);
					fd.resetAggregations();
					fd.resetSelections();
					bRefresh = true;
					if (this.shipmentSelected && !SourcingOrders.isPOPackList()) {
						this.selectShipmentTerms(currentPage, shipmentTerms);
					}
				}
			}
		}
		return (fd && !bFinish) ? wizard.setFormDataToPage(nextPage, fd, bRefresh) : null;
	},
        
	/**
         * @override
         * Filter shipment orders based on the selected shipmentTerms
         */
	getWizardViewQuery: function(wizard, view, index) {
		const po = this.getSelectedPO();
		if (index == INDEX_SHIPMENT_TERMS_VIEW) {
			return new Query().Node(xml.PROP_FLAGS, xml.OP_BIT_0, xml.NODE_FLAG_FROZEN);
		} else if (SourcingOrders.isPOPackList() && index == INDEX_SHIPMENTS_VIEW_WITH_PO && po) {
			return new Query().RefAttr("PO", xml.OP_EQUALS, po.$URL, "Child:Order");
		}
		return this.inherited(arguments);
	},
        
	/**
         * @override
         * Make shipmentTerms a ParentURL parameter
         * add po and shipments parameters to request
         */
	toWriterRequest: function() {
		const call = this.inherited<CallMethod>(arguments);

		const shipmentTerms = this.getSelectedShipmentTerms();
		if (shipmentTerms) {
			call.addParameter("ParentURL", xml.ATTR_REF, shipmentTerms.$URL);
		}
            
		const po = SourcingOrders.isPOPackList() ? this.getSelectedPO() : this.getCache().getNode(shipmentTerms.PurchasedOrders[0]);
		if (po) {
			call.addParameter("PurchasedOrder", xml.ATTR_REF, po.$URL);
		} 
            
		const shipments = this.getSelectedShipments();
		if (shipments) {
			call.addParameter("AddURLs", xml.ATTR_REFLIST, shipments);
		}

		return call;
	},
});

    export = NewPackListFromShipmentOrders;